import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { ButtonBar } from '#/components/ButtonBar/ButtonBar';
import { ClientOnly } from '#/containers/ClientOnly';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useTrackScrollTrigger } from '#/hooks/useTrackScrollTrigger';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiPost } from '#/modules/cms/api/types/CmsApiDocument';
import { CmsRecommendedJobsBlock } from '#/modules/cms/blocks/RecommendedJobs/CmsRecommendedJobsBlock';
import { CmsBodyHolder } from '#/modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsBodyHolderWidth } from '#/modules/cms/components/BodyHolder/CmsBodyHolderWidth';
import { CmsButtonBar } from '#/modules/cms/components/ButtonBar/CmsButtonBar';
import { CmsMobileTableOfContents } from '#/modules/cms/components/TableOfContents/CmsMobileTableOfContents';
import { CmsHelpPageFeedback } from '#/modules/cms/documents/Page/CmsHelpPageFeedback';
import { getCmsApiDocumentDefaultSearchType } from '#/modules/cms/helpers/getCmsApiDocumentDefaultSearchType';
import { SearchHero } from '#/modules/search/components/Hero/SearchHero';
import { DynamicFixedStaffTools } from '#/modules/staff/components/FixedStaffTools/DynamicFixedStaffTools';
import { userDashboardApiEnableProfileFeatures } from '#/modules/userDashboard/api/userDashboardApiEnableProfileFeatures';
import { useCurrentUserIsStaff } from '#/store/hooks/useCurrentUserIsStaff';
import { showModal } from '#/zustand-stores/modalStore';

import { CmsPostLayout_Box_1 } from './CmsPostLayout.styled';

type Props = { content: CmsApiPost; children: ReactNode };

export function CmsPostLayout({ content, children }: Props) {
  const { i18n } = useI18n();

  useTrackScrollTrigger('cms-content-end', 'End of Content');

  const isStaff = useCurrentUserIsStaff();
  const { user } = useUser();

  const hero = content.heroSearch?.onlyResults ? undefined : content.heroSearch;

  const showButtonBar =
    content.subsite.canSubscribe || content.hasSocialSharing;

  const bottomTableOfContents = content.tableOfContents;
  const defaultSearchType = getCmsApiDocumentDefaultSearchType({ content });

  useEffect(() => {
    if (
      content.enableProfileOnVisit &&
      user?.emailVerified &&
      !user.hasIdealistProfileFeature
    ) {
      void userDashboardApiEnableProfileFeatures().then(() => {
        showModal('CREATE_USER_PROFILE', {
          title: i18n('Create Your Profile'),
          description: i18n(
            'To continue, please review the fields below and create a public profile.',
          ),
          successMessage: i18n('Profile created.'),
        });

        return undefined;
      });
    }
  }, [
    i18n,
    content.enableProfileOnVisit,
    user?.emailVerified,
    user?.hasIdealistProfileFeature,
  ]);

  return (
    <>
      {isStaff && <DynamicFixedStaffTools content={content} />}

      {bottomTableOfContents && bottomTableOfContents.length > 0 && (
        <ClientOnly>
          <CmsMobileTableOfContents
            parents={bottomTableOfContents}
            hasButtonBar={Boolean(showButtonBar)}
          />
        </ClientOnly>
      )}

      {hero && (
        <div style={{ position: 'relative' }}>
          <SearchHero
            title={hero.text}
            image={hero.image ?? undefined}
            imageMobile={hero.imageMobile ?? undefined}
            defaultSearchType={defaultSearchType}
          />
        </div>
      )}

      {children}

      {content.hasRecommendedJobs && (
        <CmsPostLayout_Box_1>
          <CmsBodyHolder holderWidth={CmsBodyHolderWidth.NARROW_LAYOUT_WIDE}>
            <CmsRecommendedJobsBlock
              data={{
                headline: content.recommendedJobsHeadline ?? null,
                layout: 'REGULAR',
                jobFunction: null,
                areaOfFocus: null,
              }}
            />
          </CmsBodyHolder>
        </CmsPostLayout_Box_1>
      )}

      {showButtonBar && (
        <ButtonBar>
          <CmsButtonBar content={content} />
        </ButtonBar>
      )}
      <CmsHelpPageFeedback
        contentId={content.id}
        contentTitle={content.title}
      />
    </>
  );
}
